import { useRegisterSW } from 'virtual:pwa-register/react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '../../features/auth/auth-context'
import { getLastSync } from './cache'
import { probeConnectivity } from './connectivity'
import { OfflineContext, type OfflineContextValue } from './offline-context'
import { countOperations, listOperations } from './outbox'
import { processOutbox, retryAttentionOperations } from './sync'
import { installServiceWorkerUpdateChecks } from './service-worker-updates'
import type { ConnectivityState, OutboxOperation } from './types'

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [connectivity, setConnectivity] = useState<ConnectivityState>(navigator.onLine ? 'DEGRADED' : 'OFFLINE')
  const [syncing, setSyncing] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const [attentionCount, setAttentionCount] = useState(0)
  const [pendingOperations, setPendingOperations] = useState<OutboxOperation[]>([])
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [draftKeys, setDraftKeys] = useState<Set<string>>(() => new Set())
  const syncGuard = useRef(false)
  const stopServiceWorkerUpdateChecks = useRef<() => void>(() => undefined)
  const {
    offlineReady: [offlineReady],
    needRefresh: [updateAvailable],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_swScriptUrl, registration) {
      stopServiceWorkerUpdateChecks.current()
      stopServiceWorkerUpdateChecks.current = registration
        ? installServiceWorkerUpdateChecks(registration)
        : () => undefined
    },
  })

  const refreshIndicators = useCallback(async () => {
    if (!user) {
      setPendingCount(0)
      setAttentionCount(0)
      setPendingOperations([])
      setLastSyncAt(null)
      return
    }
    const [counts, operations, lastSync] = await Promise.all([
      countOperations(user.userId),
      listOperations(user.userId),
      getLastSync(user.userId),
    ])
    setPendingCount(counts.pending)
    setAttentionCount(counts.needsAttention)
    setPendingOperations(operations)
    setLastSyncAt(lastSync)
  }, [user])

  const syncNow = useCallback(async () => {
    if (!user || syncGuard.current) return
    const status = await probeConnectivity()
    setConnectivity(status)
    if (status !== 'ONLINE') {
      await refreshIndicators()
      return
    }
    syncGuard.current = true
    setSyncing(true)
    try {
      const result = await processOutbox(user)
      setLastSyncAt(new Date().toISOString())
      if (result.processed > 0 || result.needsAttention > 0) {
        window.dispatchEvent(new CustomEvent('requisicoes:sync-complete'))
      }
    } finally {
      syncGuard.current = false
      setSyncing(false)
      await refreshIndicators()
    }
  }, [refreshIndicators, user])

  const setDraftActive = useCallback((draftKey: string, active: boolean) => {
    setDraftKeys((current) => {
      if (current.has(draftKey) === active) return current
      const next = new Set(current)
      if (active) next.add(draftKey)
      else next.delete(draftKey)
      return next
    })
  }, [])

  useEffect(() => {
    const initial = window.setTimeout(() => {
      void refreshIndicators()
      void syncNow()
    }, 0)
    const onOffline = () => setConnectivity('OFFLINE')
    const onOnline = () => { setConnectivity('DEGRADED'); window.setTimeout(() => void syncNow(), 800) }
    const onChanged = () => void refreshIndicators()
    const onVisibility = () => { if (document.visibilityState === 'visible') void syncNow() }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    window.addEventListener('requisicoes:outbox-changed', onChanged)
    document.addEventListener('visibilitychange', onVisibility)
    const interval = window.setInterval(() => void syncNow(), 10_000)
    return () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('requisicoes:outbox-changed', onChanged)
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearTimeout(initial)
      window.clearInterval(interval)
    }
  }, [refreshIndicators, syncNow])

  useEffect(() => () => stopServiceWorkerUpdateChecks.current(), [])

  const value = useMemo<OfflineContextValue>(() => ({
    connectivity,
    syncing,
    pendingCount,
    attentionCount,
    pendingOperations,
    lastSyncAt,
    offlineReady,
    updateAvailable,
    hasActiveDraft: draftKeys.size > 0,
    syncNow,
    async retryPending() {
      if (!user) return
      await retryAttentionOperations(user.userId)
      await syncNow()
    },
    async applyUpdate() {
      if (draftKeys.size > 0) return
      await updateServiceWorker(true)
    },
    setDraftActive,
  }), [attentionCount, connectivity, draftKeys, lastSyncAt, offlineReady, pendingCount, pendingOperations, setDraftActive, syncNow, syncing, updateAvailable, updateServiceWorker, user])

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
}

declare global {
  interface WindowEventMap {
    'requisicoes:outbox-changed': CustomEvent
    'requisicoes:sync-complete': CustomEvent
  }
}
